import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import FaqManager from "../../components/admin/FaqManager";
import FeaturedReorder from "../../components/admin/FeaturedReorder";
import ContactCard from "../../components/admin/ContactCard";
import CampaignsManager from "../../components/admin/CampaignsManager";

const empty = { name: "", model: "", storage: "", color: "", condition: "PhoneCheck Certified", carrier: "", price: 0, lease_monthly: 0, image_url: "", description: "", featured: false, in_stock: true };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [wixMode, setWixMode] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadContacts = () => api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  const loadWixStatus = () =>
    api.get("/admin/wix-status").then((r) => { setWixMode(r.data.configured); setLastSync(r.data.last_sync); }).catch(() => {});

  const unreadCount = contacts.filter((c) => !c.read).length;
  const markAllRead = async () => {
    try {
      await api.post("/admin/contacts/mark-all-read");
      loadContacts();
      toast.success("All inquiries marked as read");
    } catch {
      toast.error("Could not update inquiries");
    }
  };

  useEffect(() => {
    if (user) { loadProducts(); loadContacts(); loadWixStatus(); }
  }, [user]);

  const syncWix = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-wix");
      setLastSync(data.last_sync);
      await loadProducts();
      toast.success(`Synced ${data.synced} products from Wix`);
    } catch {
      toast.error("Wix sync failed. Check your API key & site.");
    } finally {
      setSyncing(false);
    }
  };

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price), lease_monthly: Number(form.lease_monthly) };
    delete payload.id; delete payload.created_at;
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      loadProducts();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadProducts();
  };

  const toggleFeatured = async (p) => {
    const next = !p.featured;
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: next } : x)));
    try {
      await api.patch(`/admin/products/${p.id}/featured`, { featured: next });
      toast.success(next ? "Added to homepage" : "Removed from homepage");
    } catch {
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !next } : x)));
      toast.error("Update failed");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-alt" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-line bg-white px-6 py-4 lg:px-10">
        <div className="font-display text-lg font-black tracking-tighter text-ink">ADOPT<span className="text-crimson">-A-</span>PHONE · Admin</div>
        <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 text-sm font-bold text-ink hover:text-crimson">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
        <div className="mb-8 flex gap-2">
          <button onClick={() => setTab("products")} data-testid="tab-products" className={`rounded-full px-5 py-2 text-sm font-bold ${tab === "products" ? "bg-ink text-white" : "bg-white text-ink border border-line"}`}>Products ({products.length})</button>
          <button onClick={() => setTab("contacts")} data-testid="tab-contacts" className={`relative rounded-full px-5 py-2 text-sm font-bold ${tab === "contacts" ? "bg-ink text-white" : "bg-white text-ink border border-line"}`}>
            Inquiries ({contacts.length})
            {unreadCount > 0 && (
              <span data-testid="tab-contacts-unread" className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-crimson px-1.5 text-[11px] font-black text-white">{unreadCount}</span>
            )}
          </button>
          <button onClick={() => setTab("faq")} data-testid="tab-faq" className={`rounded-full px-5 py-2 text-sm font-bold ${tab === "faq" ? "bg-ink text-white" : "bg-white text-ink border border-line"}`}>FAQ / Chat</button>
          <button onClick={() => setTab("campaigns")} data-testid="tab-campaigns" className={`rounded-full px-5 py-2 text-sm font-bold ${tab === "campaigns" ? "bg-ink text-white" : "bg-white text-ink border border-line"}`}>Email &amp; Campaigns</button>
        </div>

        {tab === "products" && (
          <>
            {wixMode ? (
              <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-line bg-white p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="wix-banner">
                <div>
                  <div className="flex items-center gap-2 font-bold text-ink">
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">Connected to Wix</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Your catalog is managed in Wix (read-only here). {lastSync ? `Last synced ${new Date(lastSync).toLocaleString()}.` : "Not synced yet."} Use the <span className="font-semibold text-ink">Homepage</span> toggle to choose which phones show in "Popular right now".
                  </p>
                </div>
                <button onClick={syncWix} disabled={syncing} data-testid="sync-wix-btn" className="flex items-center justify-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-bold text-white hover:bg-crimson-hover disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync from Wix"}
                </button>
              </div>
            ) : (
              <div className="mb-5 flex justify-end">
                <button onClick={openNew} data-testid="add-product-btn" className="flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-bold text-white hover:bg-crimson-hover">
                  <Plus className="h-4 w-4" /> Add iPhone
                </button>
              </div>
            )}
            <FeaturedReorder products={products} category="iphone" onSaved={loadProducts} />
            <FeaturedReorder products={products} category="ipad" onSaved={loadProducts} />
            <FeaturedReorder products={products} category="macbook" onSaved={loadProducts} />
            <div className="overflow-hidden rounded-2xl border border-line bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-alt text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">SKU</th>
                    <th className="px-5 py-3">Price</th>
                    <th className="px-5 py-3">Lease/mo</th>
                    <th className="px-5 py-3">Stock</th>
                    <th className="px-5 py-3 text-center">Homepage</th>
                    {!wixMode && <th className="px-5 py-3 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-line" data-testid={`admin-row-${p.id}`}>
                      <td className="px-5 py-3 font-bold text-ink">{p.name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{p.sku || (p.variants?.length ? `${p.variants.length} SKUs` : "—")}</td>
                      <td className="px-5 py-3 font-bold text-ink">${p.price}</td>
                      <td className="px-5 py-3 font-bold text-crimson">${p.lease_monthly}</td>
                      <td className="px-5 py-3">{p.in_stock ? <span className="text-emerald-600">In stock</span> : <span className="text-muted-foreground">Out</span>}</td>
                      <td className="px-5 py-3 text-center">
                        <button
                          onClick={() => toggleFeatured(p)}
                          data-testid={`toggle-featured-${p.id}`}
                          title={p.featured ? "Showing on homepage — click to remove" : "Not on homepage — click to feature"}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${p.featured ? "bg-crimson" : "bg-line"}`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${p.featured ? "translate-x-5" : "translate-x-0.5"}`} />
                        </button>
                      </td>
                      {!wixMode && (
                        <td className="px-5 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="rounded-lg border border-line p-2 hover:border-ink"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="rounded-lg border border-line p-2 text-crimson hover:border-crimson"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.length === 0 && <div className="rounded-2xl border border-line bg-white p-8 text-center text-muted-foreground">No inquiries yet.</div>}
            {contacts.length > 0 && (
              <div className="flex items-center justify-between px-1">
                <div className="text-sm font-medium text-muted-foreground" data-testid="inquiries-summary">
                  {unreadCount > 0 ? `${unreadCount} new inquir${unreadCount === 1 ? "y" : "ies"} to review` : "You're all caught up"}
                </div>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} data-testid="mark-all-read-btn" className="rounded-full border border-line bg-white px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-ink">
                    Mark all as read
                  </button>
                )}
              </div>
            )}
            {contacts.map((c) => (
              <ContactCard key={c._id} contact={c} onChanged={loadContacts} />
            ))}
          </div>
        )}

        {tab === "faq" && <FaqManager />}
        {tab === "campaigns" && <CampaignsManager />}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="product-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-ink">{editing === "new" ? "Add iPhone" : "Edit iPhone"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="modal-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Name" v={form.name} on={set("name")} testId="f-name" full />
              <Field label="Storage" v={form.storage} on={set("storage")} testId="f-storage" />
              <Field label="Color" v={form.color} on={set("color")} testId="f-color" />
              <Field label="Price ($)" type="number" v={form.price} on={set("price")} testId="f-price" />
              <Field label="Lease /mo ($)" type="number" v={form.lease_monthly} on={set("lease_monthly")} testId="f-lease" />
              <Field label="Carrier" v={form.carrier} on={set("carrier")} testId="f-carrier" />
              <Field label="Condition" v={form.condition} on={set("condition")} testId="f-condition" />
              <Field label="Image URL" v={form.image_url} on={set("image_url")} testId="f-image" full />
              <div className="sm:col-span-2">
                <label className="text-sm font-bold text-ink">Description</label>
                <textarea rows={2} value={form.description} onChange={set("description")} data-testid="f-desc" className="mt-1 w-full resize-none rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
              </div>
              <label className="flex items-center gap-2 text-sm font-bold text-ink"><input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="f-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm font-bold text-ink"><input type="checkbox" checked={form.in_stock} onChange={set("in_stock")} data-testid="f-instock" /> In stock</label>
            </div>
            <button type="submit" data-testid="modal-save" className="mt-6 w-full rounded-full bg-crimson py-3 font-bold text-white hover:bg-crimson-hover">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, v, on, type = "text", testId, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-sm font-bold text-ink">{label}</label>
    <input type={type} value={v} onChange={on} data-testid={testId} className="mt-1 w-full rounded-xl border border-line px-3 py-2 outline-none focus:border-crimson" />
  </div>
);
